import React, {Dispatch, SetStateAction, useMemo, useRef, useState} from 'react';
import {TouchableOpacity, View} from 'react-native';
import {Text} from '../../../../shared/components/text';
import {useStyles} from '../../../../shared/hooks/styles';
import {Table as CommonTable} from '../../../../shared/components/table';
import {Column} from '../../../../shared/components/table/model';
import {datePipe, openExternalLink} from '../../../../shared/pipes';
import {Button} from '../../../../shared/components/button';
import {images} from '../../../../assets/icons';
import {usePartialList} from '../../../../shared/hooks/usePartialList';
import {useSelector} from 'react-redux';
import {dobroruDataSelector} from '../../../../redux/selectors/dobroru';
import {defaults} from '../../../../shared/constants/defaults';
import {MobileView} from './eventsTable/EventsTableMobileView';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import Tooltip from 'react-native-walkthrough-tooltip';
import {DeviceSize} from '../../../../shared/theme/helpers';

interface Props {
  setNestedComponentCoordinates: Dispatch<SetStateAction<{x: number; y: number}>>;
}

export const Table = ({setNestedComponentCoordinates}: Props) => {
  const [isTooltipShown, setIsTooltipShown] = useState(false);

  const isDesktop = useIsDesktop();

  const styles = useStyles(({theme}) => ({
    root: {},
    title: {
      marginBottom: 30,
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
    },
    empty: {
      alignItems: 'center',
    },
    emptyText: {
      color: theme.color.iconGrayColor,
    },
    th: {
      ...theme.fonts.bodySmall,
      color: theme.color.primaryBlackColor,
    },
    td: {
      ...theme.fonts.h6Light,
      color: theme.color.primaryBlackColor,
    },
    colName: {
      flex: 1,
      paddingRight: 35,
    },
    colHours: {
      flexBasis: 160,
      flexShrink: 0,
      textAlign: 'right',
      paddingRight: 40,
    },
    colDate: {
      color: theme.color.iconGrayColor,
      flexBasis: 120,
      flexShrink: 0,
    },
    loadMoreWrapper: {
      marginTop: 10,
      alignItems: 'center',
    },
    loadMoreButton: {
      borderWidth: 0,
      width: 180,
    },
  }));

  const tooltipStyles = useStyles(({theme, mediaOnly}) => ({
    icon: {
      marginLeft: 10,
      ...mediaOnly(DeviceSize.mobile, {flexBasis: '18%'}),
    },
    tooltipContentStyle: {
      display: 'flex',
      alignItems: 'flex-start',
      shadowRadius: 8,
      shadowOpacity: 0.3,
    },
    description: {
      ...mediaOnly(DeviceSize.mobile, {height: 60, width: 320}, {width: 520}),
    },
    privacySettings: {
      color: theme.color.primaryColor,
    },
  }));

  const {HelpQuestion} = images;

  const dobroruData = useSelector(dobroruDataSelector);
  const [data, showMore, allShown] = usePartialList(dobroruData?.events ?? null, defaults.partialListSizeWithButton);

  const columns: Column<{name: string; eventHours: number; date: string}>[] = useMemo(() => {
    return [
      {
        title: 'Мероприятие',
        field: 'name',
        headStyles: {colHeadStyle: styles.colName, colHeadTextStyle: styles.th},
        styles: {colBodyStyle: styles.colName, colBodyTextStyle: styles.td},
      },
      {
        title: 'Кол-во\nбаллов',
        field: 'eventScores',
        headStyles: {colHeadStyle: styles.colHours, colHeadTextStyle: styles.th},
        styles: {colBodyStyle: styles.colHours, colBodyTextStyle: styles.td},
      },
      {
        title: 'Кол-во\nчасов',
        field: 'eventHours',
        headStyles: {colHeadStyle: styles.colHours, colHeadTextStyle: styles.th},
        styles: {colBodyStyle: styles.colHours, colBodyTextStyle: styles.td},
      },
      {
        title: 'Дата ',
        render: ({date}) => (
          <Text type='h6Light' style={styles.td}>
            {datePipe(date)}
          </Text>
        ),
        field: 'date',
        headStyles: {colHeadStyle: styles.colDate, colHeadTextStyle: styles.th},
        styles: {colBodyStyle: styles.colDate},
      },
    ];
  }, [styles]);

  const elementRef = useRef<View>(null);
  type TargetElemCoords = {
    x: number;
    y: number;
  };
  const getCoordinates = (): TargetElemCoords => {
    const coords: TargetElemCoords = {
      x: 0,
      y: 0,
    };
    if (elementRef.current) {
      elementRef.current.measureInWindow((x, y) => {
        coords.x = x;
        coords.y = y;
      });
    }
    return coords;
  };
  const handleLayout = () => {
    setNestedComponentCoordinates(getCoordinates());
  };

  return (
    <>
      <View style={styles.title} onLayout={handleLayout} ref={elementRef}>
        <Text type='h3'>Мои мероприятия с портала Добро.рф</Text>
        {!data?.length && (
          <TouchableOpacity testID='volunteer-no-event-tooltip-id' style={tooltipStyles.icon}>
            <Tooltip
              onClose={() => setIsTooltipShown(false)}
              tooltipStyle={tooltipStyles.tooltipContentStyle}
              backgroundColor='transparent'
              isVisible={isTooltipShown}
              contentStyle={tooltipStyles.description}
              content={
                <Text>
                  Разрешите просматривать книжку волонтера в{' '}
                  <TouchableOpacity
                    testID='open-privacy-settings'
                    onPress={() => openExternalLink('https://dobro.website/settings/privacy')}>
                    <Text style={tooltipStyles.privacySettings} type='action'>
                      настройках приватности
                    </Text>
                  </TouchableOpacity>
                </Text>
              }>
              <TouchableOpacity onPress={() => setIsTooltipShown(true)} style={tooltipStyles.icon}>
                <HelpQuestion />
              </TouchableOpacity>
            </Tooltip>
          </TouchableOpacity>
        )}
      </View>
      {!data || !data.length ? (
        <View style={styles.empty}>
          <Text type='h5Light' style={styles.emptyText}>
            Список мероприятий пуст
          </Text>
        </View>
      ) : (
        <>
          {isDesktop ? (
            <CommonTable columns={columns} testIdPrefix='volunteers-event-row' data={data} />
          ) : (
            <MobileView data={data} />
          )}
          {!allShown && (
            <View style={styles.loadMoreWrapper}>
              <Button
                id='notifications-more'
                onPress={showMore}
                style={styles.loadMoreButton}
                text='Показать все'
                type='transparent'
                icon={images.ChevronDown}
                iconProps={{width: 24, height: 24}}
              />
            </View>
          )}
        </>
      )}
    </>
  );
};
